import * as CertificationWatermark from '@tableau/certification-watermark';
import DesignTokens from '@tableau/design-tokens';
import { DatasourceCanBaseIcon } from '@tableau/icons/DatasourceCanBaseIcon';
import { DatasourceCanExtractIcon } from '@tableau/icons/DatasourceCanExtractIcon';
import { DatasourceServerBaseIcon } from '@tableau/icons/DatasourceServerBaseIcon';
import { DatasourceServerConnectionBaseIcon } from '@tableau/icons/DatasourceServerConnectionBaseIcon';
import { DatasourceServerConnectionExtractIcon } from '@tableau/icons/DatasourceServerConnectionExtractIcon';
import { FileBaseIcon } from '@tableau/icons/FileBaseIcon';
import { SpotDatasourceTypeServerBaseIcon } from '@tableau/icons/SpotDatasourceTypeServerBaseIcon';
import { SpotDatasourceTypeServerConnectionBaseIcon } from '@tableau/icons/SpotDatasourceTypeServerConnectionBaseIcon';
import { SpotFileTypeFileBaseIcon } from '@tableau/icons/SpotFileTypeFileBaseIcon';
import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import React from 'react';
import _ from 'underscore';

import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { DatasourceNameCache, TABLEAUSERVER_CONNECTION_TYPE } from '~src/commonInfra/utils/DatasourceNameCache';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { isRemote } from '~src/datasource/dataConnection/models/ConnectionDetailInfo';
import { DataConnections } from '~src/datasource/dataConnection/resources/DataConnections';
import { filePathDisplayForFileConnections } from '~src/spacesPlatform/utils/FilePathUtils';
import { applyColor }from '~src/style/ColorService';

import { CertificationMarkTooltipCell } from '../../../catalog/components/listView/CertificationMarkTooltipCell';
import { Formatters, Messages } from '../../../codegen/Localize';
import { L10n } from '../../../commonInfra/L10n/L10n';
import { IDataConnectionInfo } from '../../../datasource/dataConnection/models/IDataConnectionInfo';
import { IDataSourceBase, IDataSourceInfo } from '../../../datasource/models/IDataSourceInfo';
import { ExplorerStyles } from '../../components/Explorer/ExplorerStyles';
import { SpacesSiteUrls } from '../spaceComponents/SpacesSiteUrls';
import { ILink, LinkWithCount } from './dataGridCells/LinkCell';
import { ITextWithIconSpecifiedByUrl, PlainTextCellWithIconAndCount } from './dataGridCells/PlainTextAndNumberCells';

export namespace ConnectionHoverPanel {

  interface TitleProps {
    count: number;
  }

  interface DetailRowProps {
    data: ConnectionDisplay.Metadata;
  }

  export function shouldDisplayPanel(datasource: IDataSourceInfo) {
    return datasource.dataConnectionsCount > 1;
  }

  function PopoverTitleElement(): React.StatelessComponent<TitleProps> {
    return function PopoverTitle(props: TitleProps) {
      const connectionCountString = Messages.connectionsCount({ COUNT: props.count,  COUNT_LOC: Formatters.formatCount(props.count) });
      return <div className="tb-content-item-popover-title">{ connectionCountString }</div>;
    };
  }

  // This is used for the data inside the tooltip from a connectsTo cell
  function ConnectionDetailRowElement(): React.StatelessComponent<DetailRowProps> {

    return function DataConnectionDetailRow(props: DetailRowProps) {
      const data = props.data;
      const text = ConnectionDisplay.translatedText(data);
      const rowHeight = CommonStyles.Sizes.CompactRowHeight;

      const ConnectionNameElement = () => <span>{ text.connectionName }</span>;

      const ConnectionTypeElement = () =>
        <span style={ { color: applyColor(DesignTokens.color.font.default.subtle, CommonStyles.Colors.Grey153)} }>{ text.connectionTypeInParentheses }</span>;

      const rowStyle = _.extend({},
        CommonStyles.Layout.TextEllipsis, {
          height: rowHeight,
          lineHeight: rowHeight + 'px',
          cursor: 'default'
        });
      const iconStyle = { ...ExplorerStyles.connectionIconStyle, verticalAlign:'text-bottom'};

      /* eslint-disable jsx-a11y/alt-text */
      return (
        <div style={ rowStyle }>
          {props.data.iconImageNode
            ? <span style={iconStyle}>{props.data.iconImageNode}</span>
            : <img src={props.data.iconImageUrl} title={ text.connectionType } style={iconStyle} />}
          <span title={ text.tooltip }>
            <ConnectionNameElement />&nbsp;<ConnectionTypeElement />
          </span>
        </div>
      );
    };
  }

  export function getTooltipContent(siteUrls: SpacesSiteUrls): (props: BodyCell.Props) => Promise<JSX.Element> {
    const ConnectionDetailRow = ConnectionDetailRowElement();
    const PopoverTitle = PopoverTitleElement();

    // This helper function returns one row of the popover content.
    function makeConnectionDetailRow(connection: IDataConnectionInfo) {
      const metadata = ConnectionDisplay.getDisplayMetadata(connection, siteUrls);
      return <ConnectionDetailRow data={ metadata } key={ connection.id } />;
    }

    // Return a function that, given the cell props, fetches data and returns the popover content.
    return (props: BodyCell.Props) => {
      return new Promise<JSX.Element>((resolve, reject) => {
        DataConnections.getFromDatasource(props.row.data as IDataSourceInfo)
          .then(connections => {
            // W-22576228: Resolve correct names for tableauserver connections (composed DS).
            DatasourceNameCache.resolveTableauServerConnections(connections);
            return connections;
          })
          .then(connections => {
            resolve(
              <div data-tb-test-id="popover-menu-panel">
                <PopoverTitle count={connections.length} />
                <div style={{ marginTop: CommonStyles.Sizes.DefaultSpace }}>
                  { _.map(connections, makeConnectionDetailRow) }
                </div>
              </div>
            );
          })
          .catch(error => reject(error));
      });
    };
  }
}

export namespace ConnectionDisplay {

  export interface Metadata {
    connectionName: string;
    connectionType: string;
    href?: string;
    iconImageUrl: string;
    /** Optional reactive @tableau/icons node rendered instead of the `iconImageUrl` <img> when present. */
    iconImageNode?: React.ReactNode;
    tooltipIconUrl: string;
    /** Optional reactive @tableau/icons node rendered instead of the `tooltipIconUrl` <img> when present. */
    tooltipIconNode?: React.ReactNode;
    cardIconUrl: string;
    /** Optional reactive @tableau/icons node rendered instead of the `cardIconUrl` <img> when present. */
    cardIconNode?: React.ReactNode;
    iconTitle?: string;
  }

  export interface TranslatedText {
    connectionName: string;
    nameUnavailable: boolean;
    connectionType: string;
    connectionTypeInParentheses: string;
    tooltip: string;
    iconTitle?: string;
  }

  export function translatedText(data: Metadata): TranslatedText {
    const connectionNameText = data.connectionName || Messages.filenameUnavailable();
    const connectionTypeText = Messages.parenthesizedArgument({ ITEM: data.connectionType});

    return {
      connectionName: connectionNameText,
      nameUnavailable: !data.connectionName,
      connectionType: data.connectionType,
      connectionTypeInParentheses: connectionTypeText,
      tooltip:  connectionNameText + ' ' + connectionNameText,
      iconTitle: data.iconTitle? data.iconTitle:data.connectionType
    };
  }

  export function getDisplayMetadata(connection: IDataSourceInfo | IDataConnectionInfo,
                                     siteUrls: SpacesSiteUrls): Metadata {
    const details = connection.connectionDetails;
    if (!details) {
      return null;
    }

    switch (details.type) {
      case TABLEAUSERVER_CONNECTION_TYPE:
        const hasExtract = (connection as IDataSourceBase).publishedDatasourceDetails ? (connection as IDataSourceBase).publishedDatasourceDetails.hasExtracts: false;
        return {
          connectionName: DatasourceNameCache.resolve(details.name) || details.name,
          connectionType: connection.connectionTypeDisplayName,
          href: siteUrls.exploreDatasourceUrl(details.id),
          iconImageUrl: hasExtract? imgPath('datasource_extract_14px_small.svg'):imgPath('tableau_server_connection_type_small.svg'),
          iconImageNode: hasExtract ? <DatasourceCanExtractIcon size={16} /> : <DatasourceCanBaseIcon size={16} />,
          cardIconUrl: hasExtract? imgPath('datasource_extract_60px_card.svg') : imgPath('tableau_server_connection_type_card.svg'),
          cardIconNode: hasExtract ? undefined : <SpotDatasourceTypeServerBaseIcon size={60} />,
          tooltipIconUrl: hasExtract? imgPath('datasource_extract_14px_small.svg'):imgPath('tableau_server_connection_type_small.svg'),
          tooltipIconNode: hasExtract ? <DatasourceCanExtractIcon size={14} /> : <DatasourceCanBaseIcon size={14} />,
          iconTitle: Messages.column_connects_to_icon_title_ds({LIVE_OR_EXTRACT:hasExtract? Messages.extract() : Messages.live()})
        };
      case 'file':
        return {
          connectionName: filePathDisplayForFileConnections(details.fileName, connection.connectionType),
          connectionType: connection.connectionTypeDisplayName,
          iconImageUrl: imgPath('file_connection_type_small.svg'),
          iconImageNode: <FileBaseIcon size={16} />,
          cardIconUrl: imgPath('file_connection_type_card.svg'),
          cardIconNode: <SpotFileTypeFileBaseIcon size={60} style={{ transform: 'scale(0.75)', transformOrigin: 'center' }} />,
          tooltipIconUrl: imgPath('file_connection_type_small.svg'),  // original: file_connection_type_small
          tooltipIconNode: <FileBaseIcon size={16} />,
          iconTitle: Messages.column_connects_to_icon_title_file({FILE_TYPE:connection.connectionTypeDisplayName})
        };
      case 'tableauserversite':
          return {
            connectionName: details.siteUrl,
            connectionType: connection.connectionTypeDisplayName,
            iconImageUrl: imgPath('external_server_connection_type_small.svg'),
            iconImageNode: <DatasourceServerBaseIcon size={14} />,
            cardIconUrl: imgPath('external_server_connection_type_card.svg'),
            cardIconNode: <SpotDatasourceTypeServerBaseIcon size={60} />,
            tooltipIconUrl: imgPath('external_server_connection_16px.svg'),
            tooltipIconNode: <DatasourceServerBaseIcon size={14} />
          };
      case 'virtualconnection':
        const isExtract = (connection as IDataSourceBase).hasExtracts ? (connection as IDataSourceBase).hasExtracts: false;
        return {
          connectionName: details.name,
          connectionType: connection.connectionTypeDisplayName,
          href: siteUrls.exploreVconnConnectionUrl(details.id),
          iconImageUrl: isExtract? imgPath('datasource_server_connection_extract_14px.svg'):imgPath('datasource_server_connection_base_14px.svg'),
          iconImageNode: isExtract ? <DatasourceServerConnectionExtractIcon size={16} /> : <DatasourceServerConnectionBaseIcon size={14} />,
          cardIconUrl: isExtract? imgPath('spot_datasource_type_server_connection_extract_60px.svg') : imgPath('spot_datasource_type_server_connection_base_60px.svg'),
          cardIconNode: isExtract ? undefined : <SpotDatasourceTypeServerConnectionBaseIcon size={60} />,
          tooltipIconUrl: isExtract? imgPath('datasource_server_connection_extract_14px.svg'):imgPath('datasource_server_connection_base_14px.svg'),
          tooltipIconNode: isExtract ? <DatasourceServerConnectionExtractIcon size={16} /> : <DatasourceServerConnectionBaseIcon size={14} />,
          iconTitle: Messages.column_connects_to_icon_title_ds({LIVE_OR_EXTRACT:isExtract? Messages.extract() : Messages.live()})
        };
      default:
        if (isRemote(details)) {
          // W-24244844 / W-23331908: fall back to caption when a database-type connector has no
          // serverName (e.g. amazon-s3 -> "<bucket>/<key> (S3)"; bigquery -> "BigQuery").
          return {
            connectionName: details.serverName
              || (details.type === 'database' ? details.caption : '')
              || '',
            connectionType: connection.connectionTypeDisplayName,
            iconImageUrl: imgPath('external_server_connection_type_small.svg'),
            iconImageNode: <DatasourceServerBaseIcon size={14} />,
            cardIconUrl: imgPath('external_server_connection_type_card.svg'),
            cardIconNode: <SpotDatasourceTypeServerBaseIcon size={60} />,
            tooltipIconUrl: imgPath('external_server_connection_16px.svg'),
            tooltipIconNode: <DatasourceServerBaseIcon size={14} />,
            iconTitle: Messages.column_connects_to_icon_title_db({DB_TYPE:connection.connectionTypeDisplayName})
          };
        } else {
          return null;
        }
    }
  }
}

export namespace ConnectsToCell {

  export const ConnectsToCellTestId = 'data-grid-cell-connects-to';

  export interface Props extends BodyCell.Props {
    siteUrls: SpacesSiteUrls;
    getPopoverContent?: (props: BodyCell.Props) => Promise<JSX.Element>;
    hideTooltips?: (data: IDataSourceInfo | IDataConnectionInfo) => boolean;
  }

  export const Cell = (props: ConnectsToCell.Props): JSX.Element => {
    const dataConnection = props.row.data as IDataSourceInfo | IDataConnectionInfo;
    const metadata = ConnectionDisplay.getDisplayMetadata(dataConnection, props.siteUrls);

    if (!metadata) {
      return null;
    }

    const translatedText = ConnectionDisplay.translatedText(metadata);
    let additionalConnectionsDisplay: string | undefined = undefined;

    if ((dataConnection as IDataSourceInfo).dataConnectionsCount) {
      const count = (dataConnection as IDataSourceInfo).dataConnectionsCount;
      additionalConnectionsDisplay = additionalConnectionsText(count);
    }

    const hasConnections = (dataConnection as IDataSourceInfo).dataConnectionsCount >= 1;
    const getPopoverContent = hasConnections ? props.getPopoverContent : undefined;

    if (metadata.href) {
      const cellData: (row: DataGrid.Row) => ILink = () => {
        return {
          text: translatedText.connectionName,
          title: translatedText.tooltip,
          url: metadata.href,
          iconUrl: metadata.iconImageUrl,
          iconNode: metadata.iconImageNode,
          iconTitle: translatedText.iconTitle? translatedText.iconTitle:translatedText.connectionType,
          style: translatedText.nameUnavailable ? MissingNameStyle : null,
          hideTooltips: true,
          testId: ConnectsToCellTestId
        };
      };

      return <LinkWithCount
        {
          ...{
            ...props,
            ...props.siteUrls,
            linkFn: cellData,
            count: additionalConnectionsDisplay,
            getPopoverContent: getPopoverContent
          }
        }
      />;

    } else {
      const cellData: ITextWithIconSpecifiedByUrl = {
        text: translatedText.connectionName,
        title: translatedText.tooltip,
        textStyle: translatedText.nameUnavailable ? MissingNameStyle : null,
        iconUrl: metadata.iconImageUrl,
        iconNode: metadata.iconImageNode,
        iconTitle: translatedText.iconTitle? translatedText.iconTitle:translatedText.connectionType,
        hideTooltips: true
      };
      return <PlainTextCellWithIconAndCount
        {
          ... {
            ...props,
            ...props.siteUrls,
            metadataFn: () => cellData,
            getPopoverContent: getPopoverContent,
            count: additionalConnectionsDisplay,
            testId: ConnectsToCellTestId
          }
        }
      />;
    }
  };

  const MissingNameStyle: React.CSSProperties = { fontStyle: 'italic', color: applyColor(DesignTokens.color.font.default.subtle, CommonStyles.Colors.Grey178) };

  export function cellFormatter(siteUrls: SpacesSiteUrls,
                                getPopoverContent?: (props: BodyCell.Props) => Promise<JSX.Element>,
                                hideTooltips?: (data: IDataSourceInfo | IDataConnectionInfo) => boolean
                              ): React.StatelessComponent<BodyCell.Props> {
    return function cell(props: BodyCell.Props): React.ReactElement<any> {
      return <ConnectsToCell.Cell {...{siteUrls, getPopoverContent, hideTooltips, ...props}} />;
    };
  }

  function additionalConnectionsText(connectionCount: number): string {
    if (connectionCount === 0 || connectionCount === 1) {
      return '';
    }

    return Messages.additionalConnectionsPillText(
      { CONNECTION_COUNT: L10n.localizedCount(connectionCount - 1) });
  }
}

export namespace CertifiableConnectsToCell {
  const WrapperStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-start'
  };

  const CertificationMarkIconStyle: React.CSSProperties = { flexShrink: 0 };

  const ConnectsToStyle: React.CSSProperties = { overflowX: 'hidden' };

  export function cellFormatter(
    isCertified: (row: DataGrid.Row) => boolean,
    getTooltipProps: (row: DataGrid.Row) => CertificationWatermark.CertificationTooltipProps,
    siteUrls: SpacesSiteUrls,
    getPopoverContent: (props: BodyCell.Props) => Promise<JSX.Element>,
    shouldDisplayPanel: (datasource: IDataSourceInfo) => boolean
  ): React.StatelessComponent<BodyCell.Props> {
    return function(props: BodyCell.Props) {
      return (
        <span style={WrapperStyle}>
          <span style={CertificationMarkIconStyle}>
            {React.createElement(CertificationMarkTooltipCell(isCertified, getTooltipProps), props)}
          </span>
          <span style={ConnectsToStyle}>
            <ConnectsToCell.Cell {...{siteUrls, getPopoverContent, hideTooltips: ConnectionHoverPanel.shouldDisplayPanel, ...props}} />
          </span>
        </span>
      );
    };
  }
}
